import { Button, Chip, Input, Spinner } from '@sjuni/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Trash2, Upload, X } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { TranslatableField } from '@/components/form/TranslatableField';
import { ApiRequestError, apiFetch } from '@/lib/api/client';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { ListResponse } from '@/lib/api/resource';

import type { MediaRow, MediaUsageRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2's `/media`, on `media.*`.
 *
 * The client's requirement is that this screen and every content editor put
 * their uploads in the same place: "when any user uploads photos for any
 * content it also should be visible in media library". There is one endpoint,
 * so that is true by construction rather than by synchronisation — this screen
 * is a view of the whole table, not a separate store.
 *
 * A grid rather than §4.6's list chrome. Every other admin list is rows of
 * words where the title is the thing you are looking for; here the picture is,
 * and a table of file names would be the wrong screen for choosing a
 * photograph.
 */
export function MediaPage() {
  const { t } = useTranslation();
  const { can } = useAuth();

  const [search, setSearch] = useState('');
  const [missingAlt, setMissingAlt] = useState(false);
  const [selected, setSelected] = useState<MediaRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const images = useQuery({
    queryKey: ['admin', 'media', search, missingAlt],
    queryFn: () =>
      apiFetch<ListResponse<MediaRow>>('/admin/media', {
        query: {
          search: search.trim() === '' ? undefined : search.trim(),
          missing_alt: missingAlt ? 1 : undefined,
          per_page: 48,
        },
      }),
  });

  const rows = images.data?.data ?? [];
  const undescribed = rows.filter((row) => !row.has_alt).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.media')}</h1>
        <p className="mt-1 max-w-prose text-body-sm text-secondary">{t('media.intro')}</p>
      </div>

      <FormError message={error} />

      <div className="flex flex-wrap items-end gap-3">
        <Input
          label={t('list.search')}
          hideLabel
          placeholder={t('media.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        {/*
          The work left, as a filter. An image with no Georgian alt text cannot
          be attached to anything, so this is the queue of images that are in
          the library and cannot yet be used.
        */}
        <label className="flex items-center gap-2 text-body-sm text-primary">
          <input
            type="checkbox"
            className="size-4 rounded border-subtle text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
            checked={missingAlt}
            onChange={(event) => setMissingAlt(event.target.checked)}
          />
          {t('media.needsAlt')}
          {undescribed > 0 && !missingAlt ? <Chip tone="gold">{String(undescribed)}</Chip> : null}
        </label>

        {can('media.create') ? <UploadButton onError={setError} /> : null}
      </div>

      {images.isPending ? <Spinner size="lg" label={t('common.loading')} /> : null}

      {!images.isPending && rows.length === 0 ? (
        <p className="rounded-card border border-subtle p-6 text-body-sm text-secondary">
          {missingAlt || search !== '' ? t('media.noMatches') : t('media.empty')}
        </p>
      ) : null}

      <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {rows.map((row) => (
          <li key={row.id} className="h-full">
            <button
              type="button"
              onClick={() => setSelected(row)}
              // `h-full` and a column: the "needs describing" line makes some
              // cards a line taller than others, and a grid of ragged cards
              // reads as a rendering fault rather than as a difference in the
              // data.
              className="group flex h-full w-full flex-col overflow-hidden rounded-card border border-subtle text-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
            >
              <span className="bg-surface-subtle block aspect-[4/3] overflow-hidden">
                <img
                  src={row.urls.thumb === '' ? row.urls.original : row.urls.thumb}
                  /*
                   * The image's own description when it has one. When it does
                   * not, empty — this is a grid of pictures being chosen
                   * between, and reading a file name aloud tells a screen
                   * reader user nothing the "needs describing" badge does not.
                   */
                  alt={row.alt.ka ?? ''}
                  loading="lazy"
                  className="size-full object-cover transition-transform group-hover:scale-105"
                />
              </span>

              <span className="block p-2">
                <span className="block truncate text-caption font-semibold text-primary">
                  {row.name}
                </span>

                {row.has_alt ? null : (
                  <span className="mt-1 inline-flex items-center gap-1 text-caption text-gold-700">
                    <AlertTriangle size={12} strokeWidth={1.75} aria-hidden="true" />
                    {t('media.noAltShort')}
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {selected ? (
        <MediaDrawer
          key={selected.id}
          image={selected}
          onClose={() => setSelected(null)}
          onDeleted={() => setSelected(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * §5.4's "drag-and-drop, multiple files" for images.
 *
 * One request per file, which is what lets a failure be reported against the
 * file that failed: a batch endpoint rejecting the ninth of ten has to either
 * lose the other nine or explain itself in a shape the client has to unpick.
 */
function UploadButton({ onError }: { onError: (message: string | null) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const inputId = useId();

  const [busy, setBusy] = useState(0);

  const upload = async (files: FileList | null) => {
    if (files === null || files.length === 0) {
      return;
    }

    onError(null);
    setBusy(files.length);

    const failed: string[] = [];

    for (const file of Array.from(files)) {
      const body = new FormData();

      body.append('file', file);

      try {
        await apiFetch('/admin/media', { method: 'POST', body });
      } catch (cause) {
        failed.push(
          cause instanceof ApiRequestError
            ? `${file.name}: ${cause.message}`
            : `${file.name}: ${t('errors.generic')}`,
        );
      }

      setBusy((count) => count - 1);
    }

    if (failed.length > 0) {
      onError(failed.join('\n'));
    }

    void queryClient.invalidateQueries({ queryKey: ['admin', 'media'] });

    if (input.current) {
      // So choosing the same file again still fires a change.
      input.current.value = '';
    }
  };

  return (
    <>
      {/*
        `sr-only` rather than `hidden`: a file input the button clicks still has
        to be reachable, and `display: none` takes it out of the accessibility
        tree along with the keyboard path to it. Which means it needs a name of
        its own — axe is right that an unlabelled input is unlabelled whether
        or not sighted users see it.
      */}
      <input
        ref={input}
        id={inputId}
        type="file"
        multiple
        // A distinct name from the button that clicks it: an `<input
        // type=file>` has the `button` role, so sharing the label would leave
        // two identically-named buttons in the tree.
        aria-label={t('media.chooseFiles')}
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="sr-only"
        onChange={(event) => void upload(event.target.files)}
      />

      <Button
        type="button"
        size="sm"
        disabled={busy > 0}
        iconLeft={<Upload size={16} strokeWidth={1.75} aria-hidden="true" />}
        onClick={() => input.current?.click()}
      >
        {busy > 0 ? t('media.uploading', { count: busy }) : t('media.upload')}
      </Button>
    </>
  );
}

/** The detail panel: describe it, see where it is used, remove it. */
function MediaDrawer({
  image,
  onClose,
  onDeleted,
}: {
  image: MediaRow;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [alt, setAlt] = useState<Record<string, string | null>>(image.alt);
  const [caption, setCaption] = useState<Record<string, string | null>>(image.caption);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [blockedBy, setBlockedBy] = useState<MediaUsageRow[] | null>(null);

  /*
   * Where it is used, read up front rather than discovered by trying to
   * delete. §5.4's document rule, and the reason it matters more here: the
   * library is shared, so the person deleting has no way of knowing which of
   * four faculties put this photograph on a page.
   */
  const usage = useQuery({
    queryKey: ['admin', 'media', image.id, 'usage'],
    queryFn: () => apiFetch<{ data: MediaUsageRow[] }>(`/admin/media/${image.id}/usage`),
  });

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/admin/media/${image.id}`, { method: 'PUT', body: { alt, caption } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'media'] });
      onClose();
    },
    onError: (cause: unknown) =>
      setError(cause instanceof ApiRequestError ? cause.message : t('errors.generic')),
  });

  const remove = useMutation({
    mutationFn: () => apiFetch(`/admin/media/${image.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'media'] });
      onDeleted();
    },
    onError: (cause: unknown) => {
      /*
       * 409 is the "in use" answer, and it carries the list. Shown in place of
       * a generic failure, because the useful message is not "could not
       * delete" but "these four pages are using it".
       */
      if (cause instanceof ApiRequestError && cause.status === 409) {
        const used = (cause.payload as { used_by?: MediaUsageRow[] } | undefined)?.used_by;

        setBlockedBy(used ?? []);
        setError(cause.message);
      } else {
        setError(cause instanceof ApiRequestError ? cause.message : t('errors.generic'));
      }

      setConfirming(false);
    },
  });

  const usedBy = blockedBy ?? usage.data?.data ?? [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="media-heading"
      className="fixed inset-y-0 end-0 z-40 flex w-full max-w-xl flex-col overflow-y-auto border-s border-subtle bg-surface shadow-xl"
    >
      <div className="flex items-start justify-between gap-3 border-b border-subtle p-5">
        <h2 id="media-heading" className="min-w-0 text-h4 font-extrabold text-primary">
          <span className="block truncate">{image.name}</span>
        </h2>

        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label={t('common.close')}
          onClick={onClose}
        >
          <X size={16} strokeWidth={1.75} aria-hidden="true" />
        </Button>
      </div>

      <div className="space-y-5 p-5">
        <FormError message={error} />

        <img
          src={image.urls.card === '' ? image.urls.original : image.urls.card}
          alt={image.alt.ka ?? ''}
          className="bg-surface-subtle max-h-72 w-full rounded-card object-contain"
        />

        {/*
          The client's rule, said on the screen rather than only enforced: an
          image cannot be attached to content until this field has Georgian.
        */}
        {image.has_alt ? null : (
          <p className="flex items-start gap-2 rounded-card border border-subtle bg-surface-alt p-3 text-body-sm text-secondary">
            <AlertTriangle
              size={16}
              strokeWidth={1.75}
              aria-hidden="true"
              className="mt-0.5 shrink-0 text-gold-700"
            />
            {t('media.altRequired')}
          </p>
        )}

        {/*
          Not marked required, though the Georgian is what an image needs
          before it can be used. The asterisk on this form would be a lie
          about *this* form — saving an undescribed image is allowed and is
          how bulk uploads work. The notice above says where the requirement
          actually bites, which is at the moment of attaching it to content.
        */}
        <TranslatableField
          label={t('media.fields.alt')}
          hint={t('media.fields.altHint')}
          value={alt}
          onChange={setAlt}
          name="alt"
          required={false}
          multiline
          rows={2}
        />

        <TranslatableField
          label={t('media.fields.caption')}
          hint={t('media.fields.captionHint')}
          value={caption}
          onChange={setCaption}
          name="caption"
          required={false}
          multiline
          rows={2}
        />

        <dl className="grid grid-cols-2 gap-3 text-caption">
          <div>
            <dt className="text-muted">{t('media.fields.uploadedBy')}</dt>
            <dd className="text-primary">{image.uploaded_by?.name ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted">{t('media.fields.added')}</dt>
            <dd className="text-primary">
              {image.created_at ? new Date(image.created_at).toLocaleDateString(locale) : '—'}
            </dd>
          </div>
        </dl>

        <section aria-labelledby="media-usage" className="border-t border-subtle pt-4">
          <h3 id="media-usage" className="text-body-sm font-extrabold text-primary">
            {t('media.usage.title')}
          </h3>

          {usedBy.length === 0 ? (
            <p className="mt-1 text-caption text-muted">{t('media.usage.none')}</p>
          ) : (
            <ul className="mt-2 space-y-1">
              {usedBy.map((row) => (
                <li key={`${row.type}-${row.id}`} className="flex flex-wrap items-baseline gap-2">
                  <Chip tone="neutral">{t(`media.usage.types.${row.type}`, row.type)}</Chip>
                  <span className="text-caption text-secondary">{row.label}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? t('common.saving') : t('common.save')}
          </Button>

          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>

          {can('media.delete') ? (
            <Button
              type="button"
              variant="ghost"
              iconLeft={<Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />}
              // Absent rather than present-and-refused when the image is in
              // use: the panel above already says what is using it.
              disabled={remove.isPending || usedBy.length > 0}
              onClick={() => setConfirming(true)}
            >
              {t('common.delete')}
            </Button>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        title={t('media.delete.title')}
        body={t('media.delete.body', { name: image.name })}
        confirmLabel={t('common.delete')}
        busy={remove.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
}

export default MediaPage;
